import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

// Show a short list first. 173 categories as a flat checkbox wall is unusable -
// on a phone it pushed every product below the fold.
const CAT_PREVIEW = 10;
const BRAND_PREVIEW = 12;

function ProductListSideBar({ filterForm, handleChangeMultiFilter, clearFilters }) {
  const [types, setTypes] = useState([]);
  const [cats, setCats] = useState([]);
  const [facets, setFacets] = useState({ brands: [], stores: [] });
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [catQ, setCatQ] = useState("");
  const [showAllCats, setShowAllCats] = useState(false);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/Public/getTypes`)
      .then((r) => setTypes(r.data || [])).catch(() => {});
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/Public/getCategories`)
      .then((r) => setCats(r.data || [])).catch(() => {});
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/Public/getFacets`)
      .then((r) => setFacets({ brands: (r.data && r.data.brands) || [], stores: (r.data && r.data.stores) || [] }))
      .catch(() => {});
  }, []);

  const selT = (filterForm.type_id || []).map(String);
  const selC = (filterForm.category_id || []).map(String);
  const selB = (filterForm.brand || []).map(String);
  const selS = (filterForm.company_id || []).map(String);
  const active = selT.length + selC.length + selB.length + selS.length;

  // only show categories belonging to selected types (if any), then text-filter
  const selTKey = selT.join(",");
  const matching = useMemo(() => {
    const keep = selTKey ? selTKey.split(",") : [];
    const forTypes = keep.length ? cats.filter((c) => keep.includes(String(c.item_type_id))) : cats;
    return forTypes
      .filter((c) => (c.name || "").toLowerCase().includes(catQ.toLowerCase()))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [cats, selTKey, catQ]);

  // Keep a ticked category visible even if it falls outside the preview,
  // otherwise un-ticking it is impossible without expanding the list.
  const selCKey = selC.join(",");
  const visibleCats = useMemo(() => {
    if (catQ || showAllCats) return matching;
    const head = matching.slice(0, CAT_PREVIEW);
    const shown = new Set(head.map((c) => String(c.id)));
    const chosen = selCKey ? selCKey.split(",") : [];
    return head.concat(
      matching.filter((c) => chosen.includes(String(c.id)) && !shown.has(String(c.id)))
    );
  }, [matching, catQ, showAllCats, selCKey]);

  const hiddenCount = matching.length - visibleCats.length;

  return (
    <>
      <div className="oot-side-head">
        <span>Filters</span>
        {active > 0 && <button className="oot-clear" onClick={clearFilters}>Clear ({active})</button>}
      </div>

      <div className="oot-filter-group">
        <p className="oot-filter-title">Product type</p>
        {types.map((t) => (
          <label className="oot-check" key={t.id}>
            <input type="checkbox" checked={selT.includes(String(t.id))}
              onChange={() => handleChangeMultiFilter("type_id", t.id)} />
            {t.name}
          </label>
        ))}
        {types.length === 0 && <p className="oot-muted-sm">Loading…</p>}
      </div>

      <div className="oot-filter-group">
        <p className="oot-filter-title">Store</p>
        {facets.stores.map((s) => (
          <label className="oot-check" key={s.id}>
            <input type="checkbox" checked={selS.includes(String(s.id))}
              onChange={() => handleChangeMultiFilter("company_id", s.id)} />
            {s.name} <span className="oot-muted-sm">({s.n})</span>
          </label>
        ))}
        {facets.stores.length === 0 && <p className="oot-muted-sm">Loading…</p>}
      </div>

      <div className="oot-filter-group">
        <p className="oot-filter-title">Brand</p>
        <div className="oot-filter-scroll">
          {(showAllBrands ? facets.brands
            : facets.brands.slice(0, BRAND_PREVIEW).concat(
                facets.brands.slice(BRAND_PREVIEW).filter((b) => selB.includes(String(b.brand)))
              )
          ).map((b) => (
            <label className="oot-check" key={b.brand}>
              <input type="checkbox" checked={selB.includes(String(b.brand))}
                onChange={() => handleChangeMultiFilter("brand", b.brand)} />
              {b.brand} <span className="oot-muted-sm">({b.n})</span>
            </label>
          ))}
        </div>
        {!showAllBrands && facets.brands.length > BRAND_PREVIEW && (
          <button className="oot-linkbtn" onClick={() => setShowAllBrands(true)}>
            Show all {facets.brands.length} brands
          </button>
        )}
        {showAllBrands && facets.brands.length > BRAND_PREVIEW && (
          <button className="oot-linkbtn" onClick={() => setShowAllBrands(false)}>Show fewer</button>
        )}
      </div>

      <div className="oot-filter-group">
        <p className="oot-filter-title">Category</p>
        <input className="oot-input oot-cat-search" placeholder="Search categories…"
          value={catQ} onChange={(e) => setCatQ(e.target.value)} />
        <div className="oot-filter-scroll">
          {visibleCats.map((c) => (
            <label className="oot-check" key={c.id}>
              <input type="checkbox" checked={selC.includes(String(c.id))}
                onChange={() => handleChangeMultiFilter("category_id", c.id)} />
              {c.name}
            </label>
          ))}
          {matching.length === 0 && <p className="oot-muted-sm">No matching categories</p>}
        </div>
        {!catQ && hiddenCount > 0 && (
          <button className="oot-linkbtn" onClick={() => setShowAllCats(true)}>
            Show all {matching.length} categories
          </button>
        )}
        {!catQ && showAllCats && matching.length > CAT_PREVIEW && (
          <button className="oot-linkbtn" onClick={() => setShowAllCats(false)}>Show fewer</button>
        )}
      </div>
    </>
  );
}

export default ProductListSideBar;
